import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Session } from "../types";
import { useSessionStore } from "../store/sessionStore";
import {
  RECENT_SESSION_COUNT,
  isTruncated,
  sessionDisplayName,
  visibleSessions,
} from "./sessionListModel";

interface SessionListProps {
  sessions: Session[];
  activeSessionId: string | null;
  scenarioTitle: string | undefined;
  onNewSession: () => void;
  onRenameSession: (sessionId: string, newName: string | null) => Promise<void>;
  onDeleteSession: (sessionId: string) => Promise<void>;
}

const SessionList: React.FC<SessionListProps> = ({
  sessions,
  activeSessionId,
  scenarioTitle,
  onNewSession,
  onRenameSession,
  onDeleteSession,
}) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug: string }>();

  const menuOpenId = useSessionStore((s) => s.menuOpenId);
  const setMenuOpenId = useSessionStore((s) => s.setMenuOpenId);
  const editingSessionId = useSessionStore((s) => s.editingSessionId);
  const setEditingSessionId = useSessionStore((s) => s.setEditingSessionId);
  const editingName = useSessionStore((s) => s.editingName);
  const setEditingName = useSessionStore((s) => s.setEditingName);

  const shown = visibleSessions(sessions, expanded);
  const truncated = isTruncated(sessions, expanded);

  // Restores the dismissal SideNav had: without it the row menu stays open
  // over the rest of the UI until you happen to click another kebab. The ref
  // goes on the whole row, not the popover, so the mousedown that closes an
  // open menu is not also counted as an outside click on its own trigger.
  const openMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpenId) return;
    const onPointerDown = (e: MouseEvent) => {
      if (openMenuRef.current && !openMenuRef.current.contains(e.target as Node)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [menuOpenId, setMenuOpenId]);

  const commitRename = async (sessionId: string) => {
    const trimmed = editingName.trim();
    setEditingSessionId(null);
    await onRenameSession(sessionId, trimmed === "" ? null : trimmed);
  };

  return (
    <div className="flex flex-col min-h-0 overflow-hidden mt-1.5 border-t border-outline-variant/30">
      <div className="flex items-center gap-1.5 px-4 pt-3 pb-1.5">
        <span className="font-label text-[10px] font-bold tracking-[0.2em] uppercase text-tertiary">
          Sessions
        </span>
        <span className="ml-auto font-label text-[10px] font-bold text-slate-500 tabular-nums">
          {sessions.length}
        </span>
        <button
          type="button"
          onClick={onNewSession}
          title="New session"
          className="p-0.5 rounded text-slate-500 hover:text-on-surface hover:bg-surface-container transition-colors"
        >
          <span className="material-symbols-outlined text-[16px]">add</span>
        </button>
      </div>

      {/* The cap that keeps this reachable no matter how many sessions exist
          lives at the mount site, not here: a percentage max-height needs a
          parent with a definite height, which this component cannot
          guarantee for itself. This just grows to fill whatever box it's
          given and scrolls within it. */}
      <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-2.5 space-y-0.5">
        {shown.map((session) => {
          const isActive = session.id === activeSessionId;
          const isEditing = editingSessionId === session.id;

          if (isEditing) {
            return (
              <div key={session.id} className="px-2 py-1">
                <input
                  autoFocus
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void commitRename(session.id);
                    if (e.key === "Escape") setEditingSessionId(null);
                  }}
                  onBlur={() => void commitRename(session.id)}
                  className="w-full bg-surface-container border border-tertiary rounded px-2 py-1 font-label text-[11.5px] text-on-surface outline-none"
                />
              </div>
            );
          }

          return (
            <div
              key={session.id}
              ref={menuOpenId === session.id ? openMenuRef : undefined}
              className={`relative group flex items-center gap-1 pr-1 rounded-md transition-colors ${
                isActive ? "bg-surface-container-high" : "hover:bg-surface-container"
              }`}
            >
              <button
                type="button"
                onClick={() => navigate(`/${orgSlug}/chat/${session.id}`)}
                className={`flex-1 min-w-0 flex items-center gap-2 pl-2.5 py-1.5 text-left font-label text-[12px] transition-colors ${
                  isActive ? "text-on-surface" : "text-slate-400 group-hover:text-on-surface"
                }`}
              >
                <span className="material-symbols-outlined text-[15px] shrink-0">
                  chat_bubble
                </span>
                <span className="flex-1 min-w-0 truncate">
                  {sessionDisplayName(session, scenarioTitle)}
                </span>
              </button>

              {/* A sibling of the row button, never a child of it: interactive
                  content inside a <button> is invalid HTML, and assistive tech
                  may prune it from the accessibility tree entirely. Being a
                  sibling also removes the need to stop propagation. */}
              <button
                type="button"
                aria-label="Session options"
                aria-expanded={menuOpenId === session.id}
                onClick={() => setMenuOpenId(menuOpenId === session.id ? null : session.id)}
                className={`shrink-0 grid place-items-center p-0.5 rounded text-slate-500 hover:text-on-surface hover:bg-surface-container-highest transition-opacity ${
                  menuOpenId === session.id
                    ? "opacity-100"
                    : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                }`}
              >
                <span className="material-symbols-outlined text-[15px]">more_vert</span>
              </button>

              {menuOpenId === session.id && (
                <div className="absolute right-1 top-full mt-0.5 z-50 min-w-[132px] p-1 rounded-md bg-surface-container-high border border-outline-variant shadow-xl">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingSessionId(session.id);
                      setEditingName(sessionDisplayName(session, scenarioTitle));
                      setMenuOpenId(null);
                    }}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded font-label text-[11.5px] text-slate-300 hover:bg-surface-container transition-colors"
                  >
                    <span className="material-symbols-outlined text-[14px]">edit</span>
                    Rename
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpenId(null);
                      void onDeleteSession(session.id);
                    }}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded font-label text-[11.5px] text-error hover:bg-surface-container transition-colors"
                  >
                    <span className="material-symbols-outlined text-[14px]">delete</span>
                    Delete
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {sessions.length > RECENT_SESSION_COUNT && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="w-full text-left px-2.5 py-1.5 rounded-md font-label text-[11px] font-semibold tracking-wide text-tertiary hover:bg-surface-container transition-colors"
          >
            {truncated ? `All ${sessions.length} sessions…` : "Show fewer"}
          </button>
        )}
      </div>
    </div>
  );
};

export default SessionList;
